import type { SelectChangeEvent } from "@mui/material";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { debounce } from "throttle-debounce";
import type { AssistantInfo } from "../../../services/api/instances/assistantsApi/types";
import type { StylistValue } from "../../../services/api/instances/stylistApi/stylistTypes";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { updateStylistAction } from "../redux/actions";
import {
  selectAssistants,
  selectSelectedAssistant,
  selectSelectedStylist,
  selectStylistDateUpdate,
  selectStylistMode,
  selectStylistResultLoading,
  selectStylists,
} from "../redux/selectors";
import {
  setSelectedAssistantAction,
  setSelectedStylistAction,
  setStylistAction,
  setStylistDateUpdate,
  setStylistResultLoading,
} from "../redux/slice";
import { useStylistPrompt } from "./use-stylist-prompt";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useStylist = () => {
  const dispatch = useAppDispatch();
  const assistants = useAppSelector(selectAssistants);
  const stylistMode = useAppSelector(selectStylistMode);
  const stylists = useAppSelector(selectStylists);
  const selectedStylist = useAppSelector(selectSelectedStylist);
  const selectedAssistant = useAppSelector(selectSelectedAssistant);
  const isResultLoading = useAppSelector(selectStylistResultLoading);
  const dateUpdate = useAppSelector(selectStylistDateUpdate);
  const { postStylistMessage } = useStylistPrompt();
  const [typing, setTyping] = useState(false);

  const storage = createStorageObjectApi<StylistValue>(stylists);

  const updateStylist = useCallback(
    debounce(1000, (postObj: StorageObject<StylistValue>) => {
      dispatch(updateStylistAction(postObj));
    }),
    [],
  );

  useEffect(() => {
    if (typing && stylistMode === "auto") {
      if (!selectedAssistant) {
        dispatch(createNotification("warning", "Выберите ассистента"));
        return;
      }
      const timer = setTimeout(() => {
        setTyping(false);
        dispatch(setStylistResultLoading(true));
        postStylistMessage();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [typing, selectedStylist?.value?.stylist_object?.prompt]);

  const handleClickStylist = () => {
    if (!selectedAssistant) {
      dispatch(createNotification("warning", "Выберите ассистента"));
      return;
    }
    dispatch(setStylistResultLoading(true));
    postStylistMessage();
  };

  const handleSelectAssistant = (event: SelectChangeEvent<unknown>) => {
    const chosenAssistant = assistants.find(
      (assistant) => assistant.name === event.target.value,
    );
    dispatch(setSelectedAssistantAction(chosenAssistant as AssistantInfo));
  };

  const handleFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: keyof StorageObject<StylistValue> | keyof StylistValue,
    fieldType: "prompt" | "result" | "name",
  ) => {
    const value = event.target.value;
    const date = new Date()
      .toLocaleDateString("ru-RU", {
        hour: "numeric",
        minute: "numeric",
      })
      .replace(",", "");
    if (fieldType === "result") {
      dispatch(setStylistDateUpdate(date));
    }
    if (fieldType === "prompt") {
      setTyping(true);
    }
    switch (fieldType) {
      case "prompt":
      case "result": {
        const updateStylistValue = {
          ...selectedStylist?.value?.stylist_object,
          [field]: value,
        };
        const updatedStylist = {
          ...selectedStylist,
          value: {
            stylist_object: updateStylistValue,
          },
        };
        const updatedStylistsList = storage.updateById(
          selectedStylist?.id as string,
          {
            value: {
              stylist_object: updateStylistValue,
            } as StylistValue,
          },
          true,
        );
        updateStylist(updatedStylist as StorageObject<StylistValue>);
        dispatch(
          setStylistAction(
            updatedStylistsList as StorageObject<StylistValue>[],
          ),
        );
        dispatch(
          setSelectedStylistAction(
            updatedStylist as StorageObject<StylistValue>,
          ),
        );
        return;
      }
      case "name": {
        const updatedStylist = {
          ...selectedStylist,
          friendly_name: value,
        };
        const updatedStylistsList = storage.updateById(
          selectedStylist?.id as string,
          {
            friendly_name: value,
          },
          true,
        );
        updateStylist(updatedStylist as StorageObject<StylistValue>);
        dispatch(
          setStylistAction(
            updatedStylistsList as StorageObject<StylistValue>[],
          ),
        );
        dispatch(
          setSelectedStylistAction(
            updatedStylist as StorageObject<StylistValue>,
          ),
        );
        return;
      }
      default:
        return;
    }
  };

  const handleCleanResult = () => {
    const updatedStylist = {
      ...selectedStylist,
      value: {
        stylist_object: {
          prompt: selectedStylist?.value?.stylist_object?.prompt as string,
          result: "",
        },
      },
    };
    const updatedStylistsList = storage.updateById(
      selectedStylist?.id as string,
      {
        value: {
          stylist_object: {
            prompt: selectedStylist?.value?.stylist_object?.prompt as string,
            result: "",
          },
        },
      },
      true,
    );
    updateStylist(updatedStylist as StorageObject<StylistValue>);
    dispatch(
      setStylistAction(updatedStylistsList as StorageObject<StylistValue>[]),
    );
    dispatch(
      setSelectedStylistAction(updatedStylist as StorageObject<StylistValue>),
    );
  };

  return {
    stylistMode,
    selectedAssistant,
    typing,
    isResultLoading,
    dateUpdate,
    handleCleanResult,
    handleFieldChange,
    handleClickStylist,
    handleSelectAssistant,
  };
};
